import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import type { AddressMatchCandidate, AddressMatchResult } from '../../../utils/addressMatching';
import { findAddressMatches } from '../../../utils/addressMatching';
import { coproTypes } from '../../../utils/copros';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import type { MatchCandidate, MatchResult } from '../../../utils/nameMatching';
import {
  findAllMatches,
  getUnmatchedFileEntries,
  getUnmatchedRnicEntries,
} from '../../../utils/nameMatching';
import Badge from '../../common/Badge';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { showToast } from '../../common/Notification/ToastrNotif.module';
import Spinner from '../../common/Spinner';
import type {
  DbCoproNotInFileDecision,
  ManualTreatment,
  MatchedPair,
  NotInRnicDecision,
  RnicNotInFileDecision,
  SyndicFeedbackOverride,
} from './ImportDetail.module';
import AddressMatchStep from './manualTreatment/AddressMatchStep';
import NameMatchStep from './manualTreatment/NameMatchStep';
import ResidualReviewStep from './manualTreatment/ResidualReviewStep';
import type { StepId } from './manualTreatment/StepNavigation';
import StepNavigation from './manualTreatment/StepNavigation';
import SyndicFeedbackStep from './manualTreatment/SyndicFeedbackStep';
import TypeCheckboxStep from './manualTreatment/TypeCheckboxStep';
import TypeSelectionStep from './manualTreatment/TypeSelectionStep';
import { downloadManualTreatmentPdf, generateManualTreatmentPdfBlob } from './ManualTreatmentPdf';
import { classifyManualTreatmentForPdf } from './manualTreatmentPdfClassification';

interface CoproNotInRnic {
  immatriculation: string;
  name: string;
  rowData?: Record<string, unknown>;
  reason?: string;
  fileAddress?: string;
}

interface RnicNotInFile {
  immatriculation: string;
  name: string;
  rnicAddress: string;
}

interface DbCoproNotInFile {
  coproId: number;
  immatriculation: string;
  name: string;
  type: number | null;
}

interface FileCoproNotInDb {
  immatriculation: string;
  name: string;
  source: 'rnic_match' | 'file_only';
}

interface ComparisonData {
  coprosNotInRnic: CoproNotInRnic[];
  rnicNotInFile: RnicNotInFile[];
  dbCoprosNotInFile?: DbCoproNotInFile[];
  fileCoprosNotInDb?: FileCoproNotInDb[];
  summary: {
    totalNotInRnic: number;
    totalRnicNotInFile: number;
    totalDbNotInFile?: number;
    totalFileNotInDb?: number;
  };
  coprosOk?: {
    immatriculation: string;
    name: string;
    rnicName: string;
    rnicAddress?: string;
  }[];
}

interface ManualTreatmentTabProps {
  comparisonData: ComparisonData;
  manualTreatment: ManualTreatment | null;
  onSave: (treatment: ManualTreatment) => Promise<void>;
  saving: boolean;
  onApplyImport?: () => Promise<void>;
  applying?: boolean;
  hasOriginalFile?: boolean;
  jwt?: string | null;
  importMeta?: {
    id?: number;
    companyId?: number;
    companyName?: string;
    fileName?: string | null;
    createdAt?: string;
  };
  hasBeenApplied?: boolean;
  appliedImportId?: number | null;
  onApplyFeedback?: () => Promise<void>;
  applyingFeedback?: boolean;
}

/**
 * Generate a unique key from immatriculation and name.
 * This handles cases where immatriculation might be empty.
 */
const generateEntryKey = (immatriculation: string, name: string): string => {
  const immatPart = immatriculation?.trim() || '';
  const namePart = name?.trim() || '';
  return `${immatPart}::${namePart}`;
};

const ManualTreatmentTab: React.FC<ManualTreatmentTabProps> = ({
  comparisonData,
  manualTreatment,
  onSave,
  saving,
  onApplyImport,
  applying = false,
  hasOriginalFile = false,
  jwt,
  importMeta,
  hasBeenApplied = false,
  appliedImportId,
  onApplyFeedback,
  applyingFeedback = false,
}) => {
  // Initialize state from existing manual treatment or create new
  const [matchedPairs, setMatchedPairs] = useState<MatchedPair[]>([]);
  const [notInRnicDecisions, setNotInRnicDecisions] = useState<NotInRnicDecision[]>([]);
  const [rnicNotInFileDecisions, setRnicNotInFileDecisions] = useState<RnicNotInFileDecision[]>([]);
  const [dbCoprosNotInFileDecisions, setDbCoprosNotInFileDecisions] = useState<DbCoproNotInFileDecision[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeStep, setActiveStep] = useState<StepId>('download');
  const [autoSavingStep, setAutoSavingStep] = useState(false);
  const [rejectedAddressSuggestions, setRejectedAddressSuggestions] = useState<Set<string>>(new Set());
  
  // State for editing immatriculation
  const [editingEntryKey, setEditingEntryKey] = useState<string | null>(null);
  const [editingImmatValue, setEditingImmatValue] = useState<string>('');
  
  // State for search filters
  const [notInRnicSearch, setNotInRnicSearch] = useState<string>('');
  const [rnicNotInFileSearch, setRnicNotInFileSearch] = useState<string>('');
  
  // State for type selection dropdown (for "Forcer" action)
  const [typeDropdownEntryKey, setTypeDropdownEntryKey] = useState<string | null>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  
  // State for expanded address details in matched pairs
  const [expandedPairKey, setExpandedPairKey] = useState<string | null>(null);
  
  // Ref for scrolling to top of treatment section on step change
  const treatmentSectionRef = useRef<HTMLDivElement>(null);
  
  // State for apply import / feedback confirmation dialogs
  const [showApplyConfirmation, setShowApplyConfirmation] = useState(false);
  const [showFeedbackConfirmation, setShowFeedbackConfirmation] = useState(false);
  const [downloadCompleted, setDownloadCompleted] = useState(false);
  const [pdfErrors, setPdfErrors] = useState<string[]>([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [mailCopied, setMailCopied] = useState(false);
  const [syndicFeedbackOverrides, setSyndicFeedbackOverrides] = useState<SyndicFeedbackOverride[]>([]);
  const [coprosPerduesSearch, setCoprosPerduesSearch] = useState('');
  const hasDiffData = (comparisonData.dbCoprosNotInFile?.length ?? 0) > 0;
  const steps = useMemo(
    () => {
      const base: { id: StepId; label: string; description: string; optional?: boolean }[] = [
        { id: 'download', label: 'Télécharger les copros non rattachées à ce syndic', description: 'Exporter les copros RNIC non rattachées' },
        { id: 'name', label: 'Correspondances suggérées par nom', description: 'Validation par nom' },
        { id: 'address', label: 'Correspondance suggérée par adresse', description: 'Validation par adresse' },
        { id: 'typeCheckbox', label: 'Cocher ASL/AFUL/Tertiaire/Copro', description: 'Typage par cases à cocher' },
        { id: 'nontrouve', label: 'Non trouvé dans RNIC', description: 'Éditer immatriculation et traiter' },
        { id: 'residual', label: 'Résiduels', description: 'Derniers écarts à traiter' },
      ];
      if (hasDiffData) {
        base.push({ id: 'coprosPerdues', label: 'Copros perdues', description: 'Copros en base absentes du fichier' });
      }
      base.push(
        { id: 'summary', label: 'Bilan & exports', description: 'Synthèse finale et raccourcis' },
        { id: 'syndicFeedback', label: 'Retour syndic', description: 'Ajustements sur retour du syndic (optionnel)', optional: true },
      );
      return base;
    },
    [hasDiffData],
  );
  const stepOrder = useMemo(() => steps.map((s) => s.id), [steps]);

  const NO_IMMAT_REASON = 'Pas de numéro d\'immatriculation';
  const normalizeReason = useCallback((reason?: string) => reason?.trim() || '', []);
  const normalizeTextLoose = useCallback(
    (text?: string) => (text || '').normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '').toLowerCase().trim(),
    [],
  );
  const isNoImmatReason = useCallback((reason?: string) => {
    const normalized = normalizeReason(reason);
    // Match explicit "no immatriculation" reasons from the backend
    // Do NOT treat empty/missing reasons as "no immat" — entries with valid immat
    // may have lost their reason during save/reload serialization
    return normalized === NO_IMMAT_REASON
      || normalized.startsWith(NO_IMMAT_REASON)
      || normalized === '-'
      || normalized.toLowerCase().includes('dispensée d\'immatriculation');
  }, [normalizeReason]);
  const isImmatMissing = useCallback((entry: NotInRnicDecision) => {
    const immat = entry.immatriculation?.trim() || '';
    return immat === '' || immat === '-';
  }, []);
  const isNonAttachedReason = useCallback((reason?: string) => {
    const normalized = normalizeTextLoose(reason);
    return normalized.includes('non rattache') && normalized.includes('syndic');
  }, [normalizeTextLoose]);

  // Create a map of entry key to reason from comparison data
  const reasonMap = useMemo(() => {
    const map = new Map<string, string>();
    (comparisonData.coprosNotInRnic || []).forEach((c) => {
      if (c.reason) {
        map.set(generateEntryKey(c.immatriculation, c.name), normalizeReason(c.reason));
      }
    });
    return map;
  }, [comparisonData.coprosNotInRnic, normalizeReason]);

  // Create a map of entry key to file address from comparison data
  const fileAddressMap = useMemo(() => {
    const map = new Map<string, string>();
    (comparisonData.coprosNotInRnic || []).forEach((c) => {
      // Use fileAddress from backend if available, otherwise try to extract from rowData
      let address = c.fileAddress || '';
      if (!address && c.rowData) {
        // Fallback: try common column patterns from rowData
        if (c.rowData[1] && typeof c.rowData[1] === 'string') {
          address = c.rowData[1].trim();
        } else {
          const parts: string[] = [];
          if (c.rowData[4]) {parts.push(String(c.rowData[4]).trim());}
          if (c.rowData[7]) {parts.push(String(c.rowData[7]).trim());}
          if (c.rowData[8]) {parts.push(String(c.rowData[8]).trim());}
          address = parts.filter(Boolean).join(' ');
        }
      }
      if (address) {
        map.set(generateEntryKey(c.immatriculation, c.name), address);
      }
    });
    return map;
  }, [comparisonData.coprosNotInRnic]);

  // Create a map of entry key to RNIC address from comparison data
  const rnicAddressMap = useMemo(() => {
    const map = new Map<string, string>();
    (comparisonData.rnicNotInFile || []).forEach((c) => {
      if (c.rnicAddress) {
        map.set(generateEntryKey(c.immatriculation, c.name), c.rnicAddress);
      }
    });
    return map;
  }, [comparisonData.rnicNotInFile]);

  const successCopros = useMemo(() => {
    const seen = new Set<string>();
    return (comparisonData.coprosOk || []).reduce<
      { immatriculation: string; name: string; rnicName?: string; rnicAddress?: string }[]
    >((acc, c) => {
      if (seen.has(c.immatriculation)) {
        return acc;
      }
      seen.add(c.immatriculation);
      acc.push({
        immatriculation: c.immatriculation,
        name: c.name,
        rnicName: c.rnicName,
        rnicAddress: c.rnicAddress,
      });
      return acc;
    }, []);
  }, [comparisonData.coprosOk]);

  // Convert comparison data to match candidates
  const fileEntries: MatchCandidate[] = useMemo(() => 
    (comparisonData.coprosNotInRnic || []).map((c) => ({
      immatriculation: c.immatriculation,
      name: c.name,
      reason: c.reason,
    })),
  [comparisonData.coprosNotInRnic]);

  const rnicEntries: MatchCandidate[] = useMemo(() => 
    (comparisonData.rnicNotInFile || []).map((c) => ({
      immatriculation: c.immatriculation,
      name: c.name,
      rnicAddress: c.rnicAddress,
    })),
  [comparisonData.rnicNotInFile]);

  // Find potential matches using name matching algorithm
  const suggestedMatches: MatchResult[] = useMemo(() => 
    findAllMatches(fileEntries, rnicEntries, 0.4),
  [fileEntries, rnicEntries]);

  // Initialize state from existing manual treatment or suggested matches
  useEffect(() => {
    if (isInitialized) {return;}

    if (manualTreatment) {
      // Load existing treatment, enriching with reasons
      const normalizedPairs = (manualTreatment.matchedPairs || []).map((pair) => ({
        ...pair,
        matchSource: pair.matchSource || (pair.isManuallyLinked ? 'manual' : 'name'),
      }));
      setMatchedPairs(normalizedPairs);
      
      // Add reasons to existing notInRnicDecisions using composite key
      const decisionsWithReasons = (manualTreatment.notInRnicDecisions || []).map((d) => ({
        ...d,
        reason: normalizeReason(d.reason || reasonMap.get(generateEntryKey(d.immatriculation, d.name))),
      }));
      
      // Sort so that entries that still need treatment (pending) come first,
      // and entries with a decision (forced with type, or ignored) are sorted at the end.
      // This sorting is only applied on initial load.
      const sortedDecisions = [...decisionsWithReasons].sort((a, b) => {
        const aIsTreated = (a.decision === 'force' && a.forcedType) || a.decision === 'skip';
        const bIsTreated = (b.decision === 'force' && b.forcedType) || b.decision === 'skip';
        if (aIsTreated && !bIsTreated) {return 1;}
        if (!aIsTreated && bIsTreated) {return -1;}
        return 0;
      });
      setNotInRnicDecisions(sortedDecisions);
      
      // Sort RNIC not in file decisions similarly: pending first, treated (integrate/ignore) at the end
      const sortedRnicNotInFile = [...(manualTreatment.rnicNotInFileDecisions || [])].sort((a, b) => {
        const aIsTreated = a.decision === 'integrate' || a.decision === 'ignore';
        const bIsTreated = b.decision === 'integrate' || b.decision === 'ignore';
        if (aIsTreated && !bIsTreated) {return 1;}
        if (!aIsTreated && bIsTreated) {return -1;}
        return 0;
      });
      setRnicNotInFileDecisions(sortedRnicNotInFile);

      // Load dbCoprosNotInFile decisions or initialize from comparison data
      if (manualTreatment.dbCoprosNotInFileDecisions?.length) {
        setDbCoprosNotInFileDecisions(manualTreatment.dbCoprosNotInFileDecisions);
      } else if (comparisonData.dbCoprosNotInFile?.length) {
        setDbCoprosNotInFileDecisions(comparisonData.dbCoprosNotInFile.map((c) => ({
          coproId: c.coproId,
          immatriculation: c.immatriculation,
          name: c.name,
          type: c.type,
          decision: 'pending',
        })));
      }

      setSyndicFeedbackOverrides(manualTreatment.syndicFeedbackOverrides || []);
    } else {
      // Initialize from suggested matches
      const initialPairs: MatchedPair[] = suggestedMatches.map((match) => ({
        fileEntry: {
          name: match.fileEntry.name,
          immatriculation: match.fileEntry.immatriculation,
        },
        rnicEntry: {
          name: match.rnicEntry.name,
          immatriculation: match.rnicEntry.immatriculation,
        },
        status: 'pending',
        confidenceScore: match.confidenceScore,
        matchSource: 'name',
      }));
      setMatchedPairs(initialPairs);

      // Initialize unmatched file entries (not in RNIC)
      const unmatchedFile = getUnmatchedFileEntries(fileEntries, suggestedMatches);
      const initialNotInRnic: NotInRnicDecision[] = unmatchedFile.map((entry) => ({
        immatriculation: entry.immatriculation,
        name: entry.name,
        decision: 'pending',
        reason: normalizeReason(entry.reason),
      }));
      setNotInRnicDecisions(initialNotInRnic);

      // Initialize unmatched RNIC entries (not in file)
      const unmatchedRnic = getUnmatchedRnicEntries(rnicEntries, suggestedMatches);
      const initialRnicNotInFile: RnicNotInFileDecision[] = unmatchedRnic.map((entry) => ({
        immatriculation: entry.immatriculation,
        name: entry.name,
        decision: 'pending',
      }));
      setRnicNotInFileDecisions(initialRnicNotInFile);

      // Initialize dbCoprosNotInFile decisions from comparison data
      if (comparisonData.dbCoprosNotInFile?.length) {
        setDbCoprosNotInFileDecisions(comparisonData.dbCoprosNotInFile.map((c) => ({
          coproId: c.coproId,
          immatriculation: c.immatriculation,
          name: c.name,
          type: c.type,
          decision: 'pending',
        })));
      }
    }

    setIsInitialized(true);
  }, [manualTreatment, suggestedMatches, fileEntries, rnicEntries, isInitialized, reasonMap, comparisonData.dbCoprosNotInFile]);

  // Handle match pair status change - use composite key as identifier
  const handleMatchStatusChange = useCallback((
    fileKey: string,
    rnicKey: string,
    status: 'validated' | 'rejected' | 'pending',
  ) => {
    setMatchedPairs((prev) => {
      const updated = prev.map((pair) => {
        const pairFileKey = generateEntryKey(pair.fileEntry.immatriculation, pair.fileEntry.name);
        const pairRnicKey = generateEntryKey(pair.rnicEntry.immatriculation, pair.rnicEntry.name);
        if (pairFileKey === fileKey && pairRnicKey === rnicKey) {
          return { ...pair, status };
        }
        return pair;
      });

      // If rejected, add entries back to their respective unmatched lists
      if (status === 'rejected') {
        const rejectedPair = prev.find((p) => {
          const pairFileKey = generateEntryKey(p.fileEntry.immatriculation, p.fileEntry.name);
          const pairRnicKey = generateEntryKey(p.rnicEntry.immatriculation, p.rnicEntry.name);
          return pairFileKey === fileKey && pairRnicKey === rnicKey;
        });
        
        if (rejectedPair) {
          const rejectedFileKey = generateEntryKey(rejectedPair.fileEntry.immatriculation, rejectedPair.fileEntry.name);
          const rejectedRnicKey = generateEntryKey(rejectedPair.rnicEntry.immatriculation, rejectedPair.rnicEntry.name);

          setNotInRnicDecisions((prevDecisions) => {
            // Check if not already in the list using composite key
            const alreadyExists = prevDecisions.some(
              (d) => generateEntryKey(d.immatriculation, d.name) === rejectedFileKey,
            );
            if (!alreadyExists) {
              return [...prevDecisions, {
                immatriculation: rejectedPair.fileEntry.immatriculation,
                name: rejectedPair.fileEntry.name,
                decision: 'pending',
                reason: reasonMap.get(rejectedFileKey),
              }];
            }
            return prevDecisions;
          });

          setRnicNotInFileDecisions((prevDecisions) => {
            // Check if not already in the list using composite key
            const alreadyExists = prevDecisions.some(
              (d) => generateEntryKey(d.immatriculation, d.name) === rejectedRnicKey,
            );
            if (!alreadyExists) {
              return [...prevDecisions, {
                immatriculation: rejectedPair.rnicEntry.immatriculation,
                name: rejectedPair.rnicEntry.name,
                decision: 'pending',
              }];
            }
            return prevDecisions;
          });
        }
      }

      return updated;
    });
  }, [reasonMap]);

  const handleAcceptAddressSuggestion = useCallback((suggestion: AddressMatchResult) => {
    const pairKey = `${generateEntryKey(suggestion.fileEntry.immatriculation, suggestion.fileEntry.name)}::${generateEntryKey(suggestion.rnicEntry.immatriculation, suggestion.rnicEntry.name)}`;
    setMatchedPairs((prev) => [
      ...prev,
      {
        fileEntry: {
          name: suggestion.fileEntry.name,
          immatriculation: suggestion.fileEntry.immatriculation,
        },
        rnicEntry: {
          name: suggestion.rnicEntry.name,
          immatriculation: suggestion.rnicEntry.immatriculation,
        },
        status: 'validated',
        confidenceScore: suggestion.confidenceScore,
        matchSource: 'address',
      },
    ]);
    setRejectedAddressSuggestions((prev) => {
      const next = new Set(prev);
      next.delete(pairKey);
      return next;
    });
  }, []);

  const handleRejectAddressSuggestion = useCallback((pairKey: string) => {
    setRejectedAddressSuggestions((prev) => {
      const next = new Set(prev);
      next.add(pairKey);
      return next;
    });
  }, []);

  // Handle not in RNIC decision change - use composite key
  const handleNotInRnicDecision = useCallback((entryKey: string, decision: 'force' | 'skip', forcedType?: number) => {
    setNotInRnicDecisions((prev) => 
      prev.map((d) => {
        if (generateEntryKey(d.immatriculation, d.name) === entryKey) {
          if (decision === 'force') {
            const nextForcedType = forcedType ?? 1; // default to copropriété
            return { ...d, decision, forcedType: nextForcedType };
          } else if (decision === 'skip') {
            // Remove forcedType when skipping
            const { forcedType: unusedType, ...rest } = d;
            void unusedType; // Explicitly ignore
            return { ...rest, decision };
          }
          return { ...d, decision };
        }
        return d;
      }),
    );
    // Close dropdown after selection
    setTypeDropdownEntryKey(null);
  }, []);

  // Handle RNIC not in file decision change - use composite key
  const handleRnicNotInFileDecision = useCallback((entryKey: string, decision: 'ignore' | 'integrate') => {
    setRnicNotInFileDecisions((prev) => 
      prev.map((d) => generateEntryKey(d.immatriculation, d.name) === entryKey ? { ...d, decision } : d),
    );
  }, []);

  // Bulk actions
  const handleBulkNotInRnicDecision = useCallback((decision: 'force' | 'skip', forcedType?: number) => {
    setNotInRnicDecisions((prev) => 
      prev.map((d) => {
        if (d.decision === 'pending') {
          if (decision === 'force') {
            const nextForcedType = forcedType ?? 1;
            return { ...d, decision, forcedType: nextForcedType };
          }
          return { ...d, decision };
        }
        return d;
      }),
    );
  }, []);

  // Handle immatriculation-missing entries type selection via checkbox table
  const handleImmatMissingTypeSelect = useCallback((entryKey: string, forcedType: number) => {
    setNotInRnicDecisions((prev) =>
      prev.map((d) => {
        if (generateEntryKey(d.immatriculation, d.name) === entryKey) {
          const isSameSelection = d.decision === 'force' && d.forcedType === forcedType;
          if (isSameSelection) {
            const { forcedType: unusedType, ...rest } = d;
            void unusedType;
            return { ...rest, decision: 'pending' };
          }
          return { ...d, decision: 'force', forcedType };
        }
        return d;
      }),
    );
  }, []);

  const handleToggleImmatMissingIgnore = useCallback((entryKey: string) => {
    setNotInRnicDecisions((prev) =>
      prev.map((d) => {
        if (generateEntryKey(d.immatriculation, d.name) === entryKey) {
          const wasSkipped = d.decision === 'skip';
          if (wasSkipped) {
            const { forcedType: unusedType, ...rest } = d;
            void unusedType;
            return { ...rest, decision: 'pending' };
          }
          return { ...d, decision: 'skip', forcedType: undefined };
        }
        return d;
      }),
    );
  }, []);
  
  // State for bulk force type selection
  const [bulkForceDropdownOpen, setBulkForceDropdownOpen] = useState(false);
  const bulkForceDropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setTypeDropdownEntryKey(null);
      }
      if (bulkForceDropdownRef.current && !bulkForceDropdownRef.current.contains(event.target as Node)) {
        setBulkForceDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBulkRnicNotInFileDecision = useCallback((decision: 'ignore' | 'integrate') => {
    setRnicNotInFileDecisions((prev) => 
      prev.map((d) => d.decision === 'pending' ? { ...d, decision } : d),
    );
  }, []);

  // Start editing immatriculation for an entry
  const handleStartEditImmat = useCallback((entryKey: string, currentImmat: string) => {
    setEditingEntryKey(entryKey);
    setEditingImmatValue(currentImmat);
  }, []);

  // Cancel editing
  const handleCancelEditImmat = useCallback(() => {
    setEditingEntryKey(null);
    setEditingImmatValue('');
  }, []);

  // Confirm immatriculation correction
  const handleConfirmImmatCorrection = useCallback((entryKey: string) => {
    const correctedImmat = editingImmatValue.trim();
    
    // Find if this corrected immatriculation matches any RNIC entry
    const matchingRnicEntry = rnicNotInFileDecisions.find(
      (rnic) => rnic.immatriculation.toLowerCase() === correctedImmat.toLowerCase(),
    );

    if (matchingRnicEntry) {
      // Find the file entry being corrected
      const fileEntry = notInRnicDecisions.find(
        (d) => generateEntryKey(d.immatriculation, d.name) === entryKey,
      );

      if (fileEntry) {
        // Create a new matched pair - store ORIGINAL immatriculation to match with notInRnicDecisions
        const newPair: MatchedPair = {
          fileEntry: {
            name: fileEntry.name,
            immatriculation: fileEntry.immatriculation, // Keep original for key matching
            originalImmatriculation: fileEntry.immatriculation, // Track original value
          },
          rnicEntry: {
            name: matchingRnicEntry.name,
            immatriculation: matchingRnicEntry.immatriculation,
          },
          status: 'validated', // Auto-validate since user corrected it
          confidenceScore: 100, // 100% confidence since manually corrected
          isManuallyLinked: true, // Flag to indicate this was manually linked
          matchSource: 'manual',
        };

        // Add to matched pairs
        setMatchedPairs((prev) => [...prev, newPair]);

        // Update the file entry with corrected immatriculation and mark as handled
        setNotInRnicDecisions((prev) => 
          prev.map((d) => {
            if (generateEntryKey(d.immatriculation, d.name) === entryKey) {
              return { ...d, correctedImmatriculation: correctedImmat, decision: 'force', forcedType: 1 };
            }
            return d;
          }),
        );

        // Remove the RNIC entry from the list (mark as handled)
        const rnicKey = generateEntryKey(matchingRnicEntry.immatriculation, matchingRnicEntry.name);
        setRnicNotInFileDecisions((prev) =>
          prev.map((d) => {
            if (generateEntryKey(d.immatriculation, d.name) === rnicKey) {
              return { ...d, decision: 'integrate' };
            }
            return d;
          }),
        );
      }
    } else {
      // No match found, just update the corrected immatriculation
      setNotInRnicDecisions((prev) => 
        prev.map((d) => {
          if (generateEntryKey(d.immatriculation, d.name) === entryKey) {
            return { ...d, correctedImmatriculation: correctedImmat, decision: 'force', forcedType: 1 };
          }
          return d;
        }),
      );
    }

    // Clear editing state
    setEditingEntryKey(null);
    setEditingImmatValue('');
  }, [editingImmatValue, notInRnicDecisions, rnicNotInFileDecisions]);

  // Get entry keys that are being handled in matched pairs (pending or validated)
  // For suggested matches (NOT manually linked), hide file entries from "Fichier non trouvé" list
  // For manually linked pairs, keep file entries visible in their list (user wants to see them)
  const handledFileKeys = useMemo(() => {
    return new Set(
      matchedPairs
        .filter((p) => (p.status === 'pending' || p.status === 'validated') && !p.isManuallyLinked)
        .map((p) => {
          // Use original immatriculation if available (for manually corrected entries)
          const immat = p.fileEntry.originalImmatriculation || p.fileEntry.immatriculation;
          return generateEntryKey(immat, p.fileEntry.name);
        }),
    );
  }, [matchedPairs]);

  // For RNIC entries, hide them from "RNIC absent du fichier" list when:
  // - They're part of a pending/validated suggested match, OR
  // - They've been manually linked (user corrected immat to match this RNIC entry)
  const handledRnicKeys = useMemo(() => {
    return new Set(
      matchedPairs
        .filter((p) => p.status === 'pending' || p.status === 'validated')
        .map((p) => generateEntryKey(p.rnicEntry.immatriculation, p.rnicEntry.name)),
    );
  }, [matchedPairs]);

  // Filter visible lists - exclude entries that are being handled in matched pairs
  const visibleNotInRnicDecisions = useMemo(() => 
    notInRnicDecisions.filter((d) => !handledFileKeys.has(generateEntryKey(d.immatriculation, d.name))),
  [notInRnicDecisions, handledFileKeys]);

  const visibleRnicNotInFileDecisions = useMemo(() => 
    rnicNotInFileDecisions.filter((d) => !handledRnicKeys.has(generateEntryKey(d.immatriculation, d.name))),
  [rnicNotInFileDecisions, handledRnicKeys]);

  // Step 1 (download): Non-attached copros
  // Show ALL entries with non-attached reason, including those in matched pairs.
  // An entry with "non rattaché à ce syndic" reason should ALWAYS appear in step 1,
  // regardless of whether it was name-matched to an RNIC entry.
  const nonAttachedNotInRnicDecisions = useMemo(() => {
    // Get non-attached entries from notInRnicDecisions (including those hidden by matching)
    const fromNotInRnic = notInRnicDecisions.filter((d) => isNonAttachedReason(d.reason));
    
    // Also include file entries from matchedPairs that have non-attached reason
    // These entries were name-matched but should still appear in step 1
    const fromMatchedPairs: NotInRnicDecision[] = matchedPairs
      .filter((pair) => {
        // Get the reason from the original comparison data using the file entry key
        const fileKey = generateEntryKey(
          pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation,
          pair.fileEntry.name,
        );
        const reason = reasonMap.get(fileKey);
        return isNonAttachedReason(reason);
      })
      .map((pair) => {
        const fileKey = generateEntryKey(
          pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation,
          pair.fileEntry.name,
        );
        // Find existing decision if any, otherwise create a new one
        const existingDecision = notInRnicDecisions.find(
          (d) => generateEntryKey(d.immatriculation, d.name) === fileKey,
        );
        if (existingDecision) {
          return existingDecision;
        }
        // Create a decision-like object from the matched pair
        return {
          immatriculation: pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation,
          name: pair.fileEntry.name,
          decision: 'pending' as const,
          reason: reasonMap.get(fileKey) || '',
        };
      });
    
    // Combine and deduplicate by key
    const seen = new Set<string>();
    const combined: NotInRnicDecision[] = [];
    
    for (const entry of [...fromNotInRnic, ...fromMatchedPairs]) {
      const key = generateEntryKey(entry.immatriculation, entry.name);
      if (!seen.has(key)) {
        seen.add(key);
        combined.push(entry);
      }
    }
    
    return combined;
  }, [notInRnicDecisions, matchedPairs, reasonMap, isNonAttachedReason]);

  // Helper: keys of entries treated in step 1 (download)
  // Now uses nonAttachedNotInRnicDecisions which includes entries from matched pairs
  const treatedInStep1Keys = useMemo(() => {
    const keys = new Set<string>();
    nonAttachedNotInRnicDecisions.forEach((d) => {
      if (d.decision !== 'pending') {
        keys.add(generateEntryKey(d.immatriculation, d.name));
      }
    });
    return keys;
  }, [nonAttachedNotInRnicDecisions]);

  // Step 4 (typeCheckbox): Missing immatriculation entries
  // Show all entries with missing immat, excluding those treated in step 1
  // Entries treated in this step stay visible (checkbox remains checked)
  const immatMissingNotInRnicDecisions = useMemo(() => 
    visibleNotInRnicDecisions.filter((d) => {
      const key = generateEntryKey(d.immatriculation, d.name);
      // Exclude if already treated in step 1
      if (treatedInStep1Keys.has(key)) {return false;}
      // Include if has missing immatriculation
      return isNoImmatReason(d.reason) || isImmatMissing(d);
    }),
  [visibleNotInRnicDecisions, isNoImmatReason, isImmatMissing, treatedInStep1Keys]);

  // Helper: keys of entries treated in step 4 (typeCheckbox)
  const treatedInStep4Keys = useMemo(() => {
    const keys = new Set<string>();
    visibleNotInRnicDecisions.forEach((d) => {
      const isMissing = isNoImmatReason(d.reason) || isImmatMissing(d);
      const isForcedCopro = d.decision === 'force' && d.forcedType === 1;
      if (isMissing && d.decision !== 'pending' && !isForcedCopro) {
        keys.add(generateEntryKey(d.immatriculation, d.name));
      }
    });
    return keys;
  }, [visibleNotInRnicDecisions, isNoImmatReason, isImmatMissing]);

  // Step 5 (nontrouve): Other entries - exclude those from previous step categories
  // Show all remaining entries, excluding those treated in step 1 or step 4
  const otherVisibleNotInRnicDecisions = useMemo(() => 
    visibleNotInRnicDecisions.filter((d) => {
      const key = generateEntryKey(d.immatriculation, d.name);
      // Exclude if already treated in step 1 or step 4
      if (treatedInStep1Keys.has(key) || treatedInStep4Keys.has(key)) {return false;}
      // Exclude immat-missing entries (they belong to typeCheckbox step)
      const isMissing = isNoImmatReason(d.reason) || isImmatMissing(d);
      const isForcedCopro = d.decision === 'force' && d.forcedType === 1;
      if (isMissing && !isForcedCopro) {return false;}
      // Exclude non-attached entries (they belong to download step)
      if (isNonAttachedReason(d.reason)) {return false;}
      return true;
    }),
  [visibleNotInRnicDecisions, isNoImmatReason, isImmatMissing, isNonAttachedReason, treatedInStep1Keys, treatedInStep4Keys]);

  // Apply search filters to visible lists
  const filteredNotInRnicDecisions = useMemo(() => {
    if (!notInRnicSearch.trim()) {return otherVisibleNotInRnicDecisions;}
    const searchLower = notInRnicSearch.toLowerCase().trim();
    return otherVisibleNotInRnicDecisions.filter(
      (d) =>
        d.immatriculation?.toLowerCase().includes(searchLower) ||
        d.name?.toLowerCase().includes(searchLower),
    );
  }, [otherVisibleNotInRnicDecisions, notInRnicSearch]);

  const filteredRnicNotInFileDecisions = useMemo(() => {
    if (!rnicNotInFileSearch.trim()) {return visibleRnicNotInFileDecisions;}
    const searchLower = rnicNotInFileSearch.toLowerCase().trim();
    return visibleRnicNotInFileDecisions.filter(
      (d) =>
        d.immatriculation?.toLowerCase().includes(searchLower) ||
        d.name?.toLowerCase().includes(searchLower),
    );
  }, [visibleRnicNotInFileDecisions, rnicNotInFileSearch]);

  // Since otherVisibleNotInRnicDecisions now excludes non-attached entries,
  // filteredNonTrvNotInRnicDecisions is just the search-filtered version
  const filteredNonTrvNotInRnicDecisions = filteredNotInRnicDecisions;
  const totalNonTrvNotInRnicDecisions = otherVisibleNotInRnicDecisions;

  // "Copros gagnées": file entries not in DB, excluding those skipped in manual treatment.
  // Entries from coprosNotInRnic (source='file_only') that were skipped should not count as gained.
  const effectiveFileCoprosNotInDb = useMemo(() => {
    const raw = comparisonData.fileCoprosNotInDb ?? [];
    const skippedKeys = new Set<string>();
    notInRnicDecisions.forEach((d) => {
      if (d.decision === 'skip') {
        skippedKeys.add(generateEntryKey(d.immatriculation, d.name));
      }
    });
    if (skippedKeys.size === 0) { return raw; }
    return raw.filter((c) => !skippedKeys.has(generateEntryKey(c.immatriculation, c.name)));
  }, [comparisonData.fileCoprosNotInDb, notInRnicDecisions]);

  // Address-based suggestions for remaining entries
  // Only consider entries that haven't been treated in previous steps (step 1 download)
  const addressSuggestions = useMemo(() => {
    const fileCandidates: AddressMatchCandidate[] = visibleNotInRnicDecisions
      .filter((d) => {
        const key = generateEntryKey(d.immatriculation, d.name);
        // Exclude entries treated in step 1
        if (treatedInStep1Keys.has(key)) {return false;}
        // Only pending entries (not yet treated)
        return d.decision === 'pending';
      })
      .map((d) => {
        const key = generateEntryKey(d.immatriculation, d.name);
        const address = fileAddressMap.get(key);
        if (!address) {return null;}
        return {
          immatriculation: d.immatriculation,
          name: d.name,
          address,
        };
      })
      .filter((d): d is AddressMatchCandidate => Boolean(d));

    const rnicCandidates: AddressMatchCandidate[] = visibleRnicNotInFileDecisions
      .filter((d) => d.decision === 'pending') // Only pending entries
      .map((d) => {
        const key = generateEntryKey(d.immatriculation, d.name);
        const address = rnicAddressMap.get(key);
        if (!address) {return null;}
        return {
          immatriculation: d.immatriculation,
          name: d.name,
          address,
        };
      })
      .filter((d): d is AddressMatchCandidate => Boolean(d));

    const existingPairKeys = new Set(
      matchedPairs.map(
        (p) =>
          `${generateEntryKey(p.fileEntry.originalImmatriculation || p.fileEntry.immatriculation, p.fileEntry.name)}::${generateEntryKey(p.rnicEntry.immatriculation, p.rnicEntry.name)}`,
      ),
    );

    return findAddressMatches(fileCandidates, rnicCandidates, 0.55).filter((match) => {
      const pairKey = `${generateEntryKey(match.fileEntry.immatriculation, match.fileEntry.name)}::${generateEntryKey(match.rnicEntry.immatriculation, match.rnicEntry.name)}`;
      return !existingPairKeys.has(pairKey) && !rejectedAddressSuggestions.has(pairKey);
    });
  }, [visibleNotInRnicDecisions, visibleRnicNotInFileDecisions, fileAddressMap, rnicAddressMap, matchedPairs, rejectedAddressSuggestions, treatedInStep1Keys]);

  // Calculate progress
  const progress = useMemo(() => {
    const totalPairs = matchedPairs.length;
    const validatedPairs = matchedPairs.filter((p) => p.status !== 'pending').length;
    
    const totalNotInRnic = visibleNotInRnicDecisions.length;
    const decidedNotInRnic = visibleNotInRnicDecisions.filter((d) => d.decision !== 'pending').length;
    const pendingNotInRnic = totalNotInRnic - decidedNotInRnic;
    
    const totalRnicNotInFile = visibleRnicNotInFileDecisions.length;
    const decidedRnicNotInFile = visibleRnicNotInFileDecisions.filter((d) => d.decision !== 'pending').length;
    const pendingRnicNotInFile = totalRnicNotInFile - decidedRnicNotInFile;

    const totalDbNotInFile = dbCoprosNotInFileDecisions.length;
    const decidedDbNotInFile = dbCoprosNotInFileDecisions.filter((d) => d.decision !== 'pending').length;
    
    const total = totalPairs + totalNotInRnic + totalRnicNotInFile + totalDbNotInFile;
    const completed = validatedPairs + decidedNotInRnic + decidedRnicNotInFile + decidedDbNotInFile;
    const remaining = pendingNotInRnic + pendingRnicNotInFile + (totalDbNotInFile - decidedDbNotInFile);
    
    return {
      total,
      completed,
      remaining,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 100,
      isComplete: total === completed,
    };
  }, [matchedPairs, visibleNotInRnicDecisions, visibleRnicNotInFileDecisions, dbCoprosNotInFileDecisions]);

  // Export "Fichier non trouvé dans RNIC" entries to Excel
  // Export ALL visible entries (not just pending), including those with corrected immatriculations
  const handleExportNotInRnic = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];

    if (visibleNotInRnicDecisions.length === 0) {return;}

    const notInRnicData = visibleNotInRnicDecisions.map((entry) => ({
      'Immatriculation': entry.immatriculation || '',
      'Immatriculation corrigée': entry.correctedImmatriculation || '',
      'Nom': entry.name,
      'Raison': entry.reason || '',
      'Décision': entry.decision === 'pending' ? 'En attente' : entry.decision === 'force' ? 'Forcé' : 'Ignoré',
      'Type forcé': entry.forcedType ? coproTypes[entry.forcedType as keyof typeof coproTypes] || '' : '',
    }));
    
    const wsNotInRnic = XLSX.utils.json_to_sheet(notInRnicData);
    const wbNotInRnic = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wbNotInRnic, wsNotInRnic, 'Fichier non dans RNIC');
    
    // Auto-size columns
    const maxWidthNotInRnic = notInRnicData.reduce((acc, row) => {
      Object.entries(row).forEach(([key, value]) => {
        const len = Math.max(String(value).length, key.length);
        acc[key] = Math.max(acc[key] || 0, len);
      });
      return acc;
    }, {} as Record<string, number>);
    wsNotInRnic['!cols'] = Object.values(maxWidthNotInRnic).map((w) => ({ wch: Math.min(w + 2, 50) }));
    
    XLSX.writeFile(wbNotInRnic, `ecarts_fichier_non_rnic_${today}.xlsx`);
  }, [visibleNotInRnicDecisions]);

  const handleExportNonAttached = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];

    if (nonAttachedNotInRnicDecisions.length === 0) {
      showToast('warning', 'Aucune copropriété non rattachée à ce syndic à exporter.');
      return;
    }

    const data = nonAttachedNotInRnicDecisions.map((entry) => ({
      'Immatriculation': entry.immatriculation || '',
      'Immatriculation corrigée': entry.correctedImmatriculation || '',
      'Nom': entry.name,
      'Raison': entry.reason || '',
      'Décision': entry.decision === 'pending' ? 'En attente' : entry.decision === 'force' ? 'Forcé' : 'Ignoré',
      'Type forcé': entry.forcedType ? coproTypes[entry.forcedType as keyof typeof coproTypes] || '' : '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Non rattaché syndic');

    const maxWidth = data.reduce((acc, row) => {
      Object.entries(row).forEach(([key, value]) => {
        const len = Math.max(String(value).length, key.length);
        acc[key] = Math.max(acc[key] || 0, len);
      });
      return acc;
    }, {} as Record<string, number>);
    ws['!cols'] = Object.values(maxWidth).map((w) => ({ wch: Math.min(w + 2, 50) }));

    XLSX.writeFile(wb, `copros_non_rattachees_syndic_${today}.xlsx`);
    showToast('success', 'Export des copropriétés non rattachées effectué.');
  }, [nonAttachedNotInRnicDecisions]);

  const forceNonAttachedEntries = useCallback(() => {
    const matchedPairNonAttachedEntries: NotInRnicDecision[] = [];
    
    matchedPairs.forEach((pair) => {
      const fileKey = generateEntryKey(
        pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation,
        pair.fileEntry.name,
      );
      const reason = reasonMap.get(fileKey);
      if (isNonAttachedReason(reason)) {
        matchedPairNonAttachedEntries.push({
          immatriculation: pair.fileEntry.originalImmatriculation || pair.fileEntry.immatriculation,
          name: pair.fileEntry.name,
          decision: 'force',
          forcedType: 1,
          reason: reason || '',
        });
      }
    });

    setNotInRnicDecisions((prev) => {
      const updated = prev.map((d) => {
        const isTarget = isNonAttachedReason(d.reason);
        if (isTarget && d.decision === 'pending') {
          return { ...d, decision: 'force' as const, forcedType: 1 };
        }
        return d;
      });
      
      const existingKeys = new Set(updated.map((d) => generateEntryKey(d.immatriculation, d.name)));
      const toAdd = matchedPairNonAttachedEntries.filter(
        (entry) => !existingKeys.has(generateEntryKey(entry.immatriculation, entry.name)),
      );
      
      return [...updated, ...toAdd];
    });

    setDownloadCompleted(true);
  }, [isNonAttachedReason, matchedPairs, reasonMap]);

  const handleDownloadNonAttached = useCallback(async() => {
    await handleExportNonAttached();
    forceNonAttachedEntries();
  }, [handleExportNonAttached, forceNonAttachedEntries]);


  // Export "RNIC absent du fichier" entries to Excel
  // Export ALL visible entries (not just pending)
  const handleExportRnicNotInFile = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];

    if (visibleRnicNotInFileDecisions.length === 0) {return;}

    const rnicNotInFileData = visibleRnicNotInFileDecisions.map((entry) => ({
      'Immatriculation': entry.immatriculation || '',
      'Nom': entry.name,
      'Décision': entry.decision === 'pending' ? 'En attente' : entry.decision === 'integrate' ? 'Intégré' : 'Ignoré',
    }));
    
    const wsRnicNotInFile = XLSX.utils.json_to_sheet(rnicNotInFileData);
    const wbRnicNotInFile = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wbRnicNotInFile, wsRnicNotInFile, 'RNIC non dans fichier');
    
    // Auto-size columns
    const maxWidthRnic = rnicNotInFileData.reduce((acc, row) => {
      Object.entries(row).forEach(([key, value]) => {
        const len = Math.max(String(value).length, key.length);
        acc[key] = Math.max(acc[key] || 0, len);
      });
      return acc;
    }, {} as Record<string, number>);
    wsRnicNotInFile['!cols'] = Object.values(maxWidthRnic).map((w) => ({ wch: Math.min(w + 2, 50) }));
    
    XLSX.writeFile(wbRnicNotInFile, `ecarts_rnic_non_fichier_${today}.xlsx`);
  }, [visibleRnicNotInFileDecisions]);

  const handleDownloadPdfSummary = useCallback(async() => {
    setPdfErrors([]);
    setGeneratingPdf(true);
    try {
      const classification = classifyManualTreatmentForPdf({
        matchedPairs,
        notInRnicDecisions,
        rnicNotInFileDecisions,
        fileAddressMap,
        rnicAddressMap,
        importSuccessCopros: successCopros,
      });

      console.info('[ManualTreatmentPdf] classification summary', {
        adjustments: classification.adjustments.length,
        aslAfulTertiaire: classification.aslAfulTertiaire.length,
        ignoredFile: classification.ignoredFile.length,
        ignoredRnic: classification.ignoredRnic.length,
        forcedRnic: classification.forcedRnic.length,
        success: classification.success.length,
        errors: classification.errors.length,
      });

      if (classification.errors.length > 0) {
        console.warn('[ManualTreatmentPdf] blocking errors', classification.errors);
        setPdfErrors(classification.errors);
        showToast('error', 'Certaines copropriétés sont en double ou non classées. Corrigez-les avant de générer le PDF.');
        return;
      }

      const pdfParams = {
        matchedPairs,
        notInRnicDecisions,
        rnicNotInFileDecisions,
        fileAddressMap,
        rnicAddressMap,
        importSuccessCopros: successCopros,
        meta: {
          importId: importMeta?.id,
          companyName: importMeta?.companyName,
          fileName: importMeta?.fileName || undefined,
          createdAt: importMeta?.createdAt,
        },
        title: 'Synthèse de traitement manuel des copropriétés',
      };

      await downloadManualTreatmentPdf(pdfParams);
      showToast('success', 'PDF généré avec succès.');

      if (jwt && importMeta?.companyId) {
        const blob = await generateManualTreatmentPdfBlob(pdfParams);
        const pdfFile = new File([blob], 'import-report.pdf', { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('file', pdfFile);

        try {
          const response = await fetch(`/api/companies/${importMeta.companyId}/import-report-pdf`, {
            method: 'POST',
            headers: { Authorization: JWTAuthorization(jwt) },
            body: formData,
          });
          if (response.ok) {
            showToast('success', 'Rapport enregistré sur le serveur.');
          } else {
            console.error('Import report PDF upload failed:', response.status);
          }
        } catch (uploadError) {
          console.error('Import report PDF upload error:', uploadError);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la génération du PDF.';
      setPdfErrors((prev) => (prev.length > 0 ? prev : [message]));
      showToast('error', 'Impossible de générer le PDF de synthèse.');
      console.error('ManualTreatmentTab PDF generation error:', error);
    } finally {
      setGeneratingPdf(false);
    }
  }, [
    fileAddressMap,
    importMeta?.companyId,
    importMeta?.companyName,
    importMeta?.createdAt,
    importMeta?.fileName,
    importMeta?.id,
    jwt,
    matchedPairs,
    notInRnicDecisions,
    rnicAddressMap,
    rnicNotInFileDecisions,
    successCopros,
  ]);

  const handleCopyMailText = useCallback(() => {
    const classification = classifyManualTreatmentForPdf({
      matchedPairs,
      notInRnicDecisions,
      rnicNotInFileDecisions,
      fileAddressMap,
      rnicAddressMap,
      importSuccessCopros: successCopros,
    });

    const successCount = classification.success.length;
    const manualCount =
      classification.adjustments.length +
      classification.aslAfulTertiaire.length +
      classification.ignoredFile.length +
      classification.ignoredRnic.length +
      classification.forcedRnic.length;

    const lines = [
      'Objet : Rapport d\'importation et traitement des copropriétés / Points 1 à 5 à regarder et à vérifier',
      '',
      'Bonjour,',
      '',
      'Pour faire suite à votre visioconférence, je vous joins comme convenu le rapport de l\'importation des copropriétés que nous avons réalisée et des traitements effectués.',
      '',
      `Ce rapport d'importation des immeubles que vous gérez contient 6 points et l'analyse pour chaque immeuble de sa situation à ce jour dans le module CROISSANCE.`,
      '',
      `Pour ${successCount} copropriétés, nous avons pu valider le n° RNIC avec les autres informations (nom, adresse). Pour celles-là vous n'avez rien à faire. Elles sont dans le point 6. Vous pouvez néanmoins les regarder afin d'être totalement sûr.`,
      '',
      `Pour ${manualCount} copropriétés, il y a 5 points à valider en fonction du traitement et des résultats que nous avons obtenus. Vous trouverez pour chaque point, ce que nous avons fait et ce que vous devez vérifier.`,
      '',
      'Merci de valider les 5 points du rapport en pièce jointe.',
      '',
      'Point 1 : pour les immeubles présents dans ce point',
      '',
      'A - Ce que nous avons fait :',
      'Rien pour ces immeubles : il s\'agit des entrées de votre fichier que nous n\'avons pas su faire correspondre à une entrée du registre des copros ou que nous n\'avons pas pu identifier comme ASL, AFUL, immeubles tertiaires.',
      '',
      'B - Ce que vous devez faire :',
      'Nous indiquer pour chaque immeuble de ce point :',
      '- S\'il s\'agit d\'une AFUL / ASL ou d\'un immeuble tertiaire',
      '- S\'il s\'agit d\'une copropriété, nous donner le numéro d\'immatriculation (si elle n\'est pas enregistrée dans le registre : alors il faut l\'enregistrer de votre côté)',
      '',
      'Point 2 et Point 3 : pour les copropriétés présentes dans ces points',
      '',
      'A - Ce que nous avons fait :',
      'Nous avions de notre côté les copropriétés présentes dans ces points comme étant des copropriétés gérées par vous. Mais nous ne les avons pas trouvées dans le fichier que vous nous avez fourni. Nous les avons divisées en 2 points distincts car nous avons effectué des traitements différents :',
      '- Pour le point 2, nous avons ignoré ces copropriétés',
      '- Pour le point 3, nous les avons intégrées dans votre fichier pour le moment (mais sans être totalement sûr)',
      '',
      'B - Ce que vous devez faire pour ces 2 points :',
      'Valider qu\'il s\'agit bien de copropriétés :',
      '- Que vous avez perdues',
      '- Que vous gérez encore',
      '',
      'Point 4 : pour les copropriétés présentes dans ce point',
      '',
      'A - Ce que nous avons fait :',
      'Nous avons effectué un rapprochement manuel via le nom, ou bien l\'adresse de la copropriété.',
      '',
      'B - Ce que vous devez faire :',
      'La correspondance a-t-elle été faite correctement ?',
      'L\'immatriculation correspond-elle à celle dans votre logiciel ?',
      'Si vous détectez une anomalie, merci de nous indiquer le nom de la copropriété concernée et ce qu\'il faut changer pour celle-ci.',
      '',
      'Point 5 : pour les immeubles présents dans ce point',
      '',
      'A - Ce que nous avons fait :',
      'Nous avons identifié des immeubles et leur avons attribué un des statuts suivants :',
      '- ASL',
      '- AFUL',
      '- Immeuble tertiaire',
      '',
      'B - Ce que vous devez faire :',
      'Vérifier que nous avons bien attribué le bon statut ASL / AFUL / immeubles tertiaires.',
      'Si vous détectez une anomalie dans l\'attribution des immeubles listés, merci de nous indiquer le nom de l\'immeuble concerné et ce qu\'il faut changer pour celui-ci.',
      'Si vous avez d\'autres ASL, AFUL, immeubles tertiaires non présents dans ce point 5 merci de nous les indiquer également pour que nous les rajoutions.',
      '',
      'Point 6 : pour les copropriétés présentes dans ce point',
      'Il s\'agit des entrées pour lesquelles l\'import s\'est déroulé sans encombre, après une correspondance des noms et des numéros d\'immatriculation effectuée. Une vérification est souhaitable.',
      '',
      'Merci de votre retour par mail concernant ces vérifications pour que nous puissions passer à l\'étape suivante de vérification des adresses des immeubles.',
      '',
      'Restant à votre disposition',
    ];

    const text = lines.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setMailCopied(true);
      showToast('success', 'Texte du mail copié dans le presse-papiers.');
      setTimeout(() => setMailCopied(false), 3000);
    }).catch(() => {
      showToast('error', 'Impossible de copier le texte.');
    });
  }, [
    fileAddressMap,
    matchedPairs,
    notInRnicDecisions,
    rnicAddressMap,
    rnicNotInFileDecisions,
    successCopros,
  ]);

  // Save handler
  const handleSave = useCallback(async() => {
    const treatment: ManualTreatment = {
      matchedPairs,
      notInRnicDecisions,
      rnicNotInFileDecisions,
      dbCoprosNotInFileDecisions: dbCoprosNotInFileDecisions.length > 0 ? dbCoprosNotInFileDecisions : undefined,
      syndicFeedbackOverrides: syndicFeedbackOverrides.length > 0 ? syndicFeedbackOverrides : undefined,
      status: progress.isComplete ? 'completed' : 'in_progress',
    };
    await onSave(treatment);
  }, [matchedPairs, notInRnicDecisions, onSave, progress.isComplete, rnicNotInFileDecisions, dbCoprosNotInFileDecisions, syndicFeedbackOverrides]);

  // Apply import button click handler
  const handleApplyImportClick = () => {
    if (!progress.isComplete) {
      showToast('error', 'Toutes les entrées doivent être traitées avant l\'import (progression à 100%).');
      return;
    }
    if (!hasOriginalFile) {
      showToast('error', 'Le fichier original n\'est pas disponible pour cet import.');
      return;
    }
    setShowApplyConfirmation(true);
  };

  const buildTreatmentWithOverrides = useCallback((): ManualTreatment => {
    if (syndicFeedbackOverrides.length === 0) {
      return {
        matchedPairs,
        notInRnicDecisions,
        rnicNotInFileDecisions,
        dbCoprosNotInFileDecisions: dbCoprosNotInFileDecisions.length > 0 ? dbCoprosNotInFileDecisions : undefined,
        syndicFeedbackOverrides: undefined,
        status: progress.isComplete ? 'completed' : 'in_progress',
      };
    }

    const overrideByKey = new Map<string, SyndicFeedbackOverride>();
    syndicFeedbackOverrides.forEach((o) => overrideByKey.set(o.key, o));

    const mergedNotInRnic = notInRnicDecisions.map((d) => {
      const key = generateEntryKey(d.immatriculation, d.name);
      const override = overrideByKey.get(key);
      if (!override) {return d;}

      const updated = { ...d };
      if (override.newImmat !== undefined) {
        updated.correctedImmatriculation = override.newImmat;
      }
      if (override.newType !== undefined) {
        if (override.newType === 0) {
          updated.decision = 'skip' as const;
          updated.forcedType = undefined;
        } else {
          updated.decision = 'force' as const;
          updated.forcedType = override.newType;
        }
      }
      return updated;
    });

    const mergedRnicNotInFile = rnicNotInFileDecisions.map((d) => {
      const key = generateEntryKey(d.immatriculation, d.name);
      const override = overrideByKey.get(key);
      if (!override) {return d;}

      if (override.newType !== undefined) {
        return override.newType === 0
          ? { ...d, decision: 'ignore' as const }
          : { ...d, decision: 'integrate' as const };
      }
      return d;
    });

    const mergedPairs = matchedPairs.map((p) => {
      const fileKey = generateEntryKey(
        p.fileEntry.originalImmatriculation || p.fileEntry.immatriculation,
        p.fileEntry.name,
      );
      const override = overrideByKey.get(fileKey);
      if (!override?.newImmat) {return p;}

      return {
        ...p,
        fileEntry: {
          ...p.fileEntry,
          originalImmatriculation: p.fileEntry.immatriculation,
          immatriculation: override.newImmat,
        },
      };
    });

    return {
      matchedPairs: mergedPairs,
      notInRnicDecisions: mergedNotInRnic,
      rnicNotInFileDecisions: mergedRnicNotInFile,
      dbCoprosNotInFileDecisions: dbCoprosNotInFileDecisions.length > 0 ? dbCoprosNotInFileDecisions : undefined,
      syndicFeedbackOverrides,
      status: progress.isComplete ? 'completed' : 'in_progress',
    };
  }, [matchedPairs, notInRnicDecisions, rnicNotInFileDecisions, syndicFeedbackOverrides, progress.isComplete]);

  // Apply import confirmation handler
  const handleApplyImportConfirm = async() => {
    setShowApplyConfirmation(false);
    try {
      const treatment = buildTreatmentWithOverrides();
      await onSave(treatment);
      if (onApplyImport) {
        await onApplyImport();
      }
    } catch (error) {
      console.error('Error during apply import:', error);
    }
  };

  // Apply feedback button click handler
  const handleApplyFeedbackClick = () => {
    if (syndicFeedbackOverrides.length === 0) {
      showToast('warning', 'Aucune modification à appliquer.');
      return;
    }
    setShowFeedbackConfirmation(true);
  };

  // Apply feedback confirmation handler
  const handleApplyFeedbackConfirm = async() => {
    setShowFeedbackConfirmation(false);
    try {
      const treatment = buildTreatmentWithOverrides();
      await onSave(treatment);
      if (onApplyFeedback) {
        await onApplyFeedback();
      }
    } catch (error) {
      console.error('Error during apply feedback:', error);
    }
  };

  const navigateToStep = useCallback(async(target: StepId) => {
    if (target === activeStep) {return;}
    if (!isInitialized) {
      setActiveStep(target);
      return;
    }
    setAutoSavingStep(true);
    await handleSave();
    setActiveStep(target);
    setAutoSavingStep(false);
    
    // Scroll to top of treatment section for better UX
    if (treatmentSectionRef.current) {
      treatmentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeStep, handleSave, isInitialized]);

  const handleNextStep = useCallback(() => {
    const index = stepOrder.indexOf(activeStep);
    if (index < stepOrder.length - 1) {
      if (activeStep === 'download') {
        forceNonAttachedEntries();
      }
      void navigateToStep(stepOrder[index + 1]);
    }
  }, [activeStep, forceNonAttachedEntries, navigateToStep, stepOrder]);

  const handlePrevStep = useCallback(() => {
    const index = stepOrder.indexOf(activeStep);
    if (index > 0) {
      void navigateToStep(stepOrder[index - 1]);
    }
  }, [activeStep, navigateToStep, stepOrder]);

  const handleStepSelect = useCallback((step: StepId) => {
    void navigateToStep(step);
  }, [navigateToStep]);

  // Filter visible matched pairs (exclude rejected and manually linked pairs)
  // Manually linked pairs should NOT appear in "Correspondances suggérées" - they stay in their original lists
  const visibleMatchedPairs = matchedPairs.filter((p) => p.status !== 'rejected' && !p.isManuallyLinked);
  const nameMatchedPairs = useMemo(
    () => visibleMatchedPairs.filter((p) => (p.matchSource || 'name') === 'name'),
    [visibleMatchedPairs],
  );
  const addressMatchedPairs = useMemo(
    () => visibleMatchedPairs.filter((p) => p.matchSource === 'address'),
    [visibleMatchedPairs],
  );
  const addressDisplayEntries = useMemo(() => {
    const existingKeys = new Set(
      addressMatchedPairs.map(
        (p) =>
          `${generateEntryKey(p.fileEntry.immatriculation, p.fileEntry.name)}::${generateEntryKey(p.rnicEntry.immatriculation, p.rnicEntry.name)}`,
      ),
    );

    const existingEntries = addressMatchedPairs.map((p) => ({
      mode: 'existing' as const,
      status: p.status,
      pairKey: `${generateEntryKey(p.fileEntry.immatriculation, p.fileEntry.name)}::${generateEntryKey(p.rnicEntry.immatriculation, p.rnicEntry.name)}`,
      fileEntry: p.fileEntry,
      rnicEntry: p.rnicEntry,
      confidenceScore: p.confidenceScore,
    }));

    const suggestionEntries = addressSuggestions
      .map((s) => {
        const pairKey = `${generateEntryKey(s.fileEntry.immatriculation, s.fileEntry.name)}::${generateEntryKey(s.rnicEntry.immatriculation, s.rnicEntry.name)}`;
        if (existingKeys.has(pairKey)) {
          return null;
        }
        return {
          mode: 'suggestion' as const,
          status: 'pending' as const,
          pairKey,
          ...s,
        };
      })
      .filter((s): s is NonNullable<typeof s> => Boolean(s));

    return [...existingEntries, ...suggestionEntries];
  }, [addressMatchedPairs, addressSuggestions]);
  const pendingNameMatches = useMemo(
    () => nameMatchedPairs.filter((p) => p.status === 'pending'),
    [nameMatchedPairs],
  );

  const summaryStepStats = useMemo(() => {
    const baseStats = [
      {
        id: 'download' as StepId,
        label: 'Non rattachées (syndic)',
        description: 'Téléchargées et forcées en copropriété',
        total: nonAttachedNotInRnicDecisions.length,
        treated: nonAttachedNotInRnicDecisions.filter((d) => d.decision !== 'pending').length,
      },
      {
        id: 'name' as StepId,
        label: 'Correspondances par nom',
        description: 'Validations ou rejets des suggestions par nom',
        total: nameMatchedPairs.length,
        treated: nameMatchedPairs.filter((p) => p.status !== 'pending').length,
      },
      {
        id: 'address' as StepId,
        label: 'Correspondances par adresse',
        description: 'Suggestions croisées via les adresses',
        total: addressDisplayEntries.length,
        treated: addressDisplayEntries.filter((entry) => entry.status !== 'pending').length,
      },
      {
        id: 'typeCheckbox' as StepId,
        label: 'Typage ASL/AFUL/Tertiaire/Copro',
        description: 'Entrées sans immatriculation',
        total: immatMissingNotInRnicDecisions.length,
        treated: immatMissingNotInRnicDecisions.filter((d) => d.decision !== 'pending').length,
      },
      {
        id: 'nontrouve' as StepId,
        label: 'Fichier non trouvé dans RNIC',
        description: 'Corrections d\'immat ou décisions force/ignore',
        total: otherVisibleNotInRnicDecisions.length,
        treated: otherVisibleNotInRnicDecisions.filter((d) => d.decision !== 'pending').length,
      },
      {
        id: 'residual' as StepId,
        label: 'Présent RNIC mais absent du fichier',
        description: 'Intégrer ou ignorer les écarts restants',
        total: visibleRnicNotInFileDecisions.length,
        treated: visibleRnicNotInFileDecisions.filter((d) => d.decision !== 'pending').length,
      },
      ...(dbCoprosNotInFileDecisions.length > 0 ? [{
        id: 'coprosPerdues' as StepId,
        label: 'Copros perdues',
        description: 'Copros en base absentes du nouveau fichier',
        total: dbCoprosNotInFileDecisions.length,
        treated: dbCoprosNotInFileDecisions.filter((d) => d.decision !== 'pending').length,
      }] : []),
      {
        id: 'summary' as StepId,
        label: 'Progression globale',
        description: 'Toutes les catégories confondues',
        total: progress.total,
        treated: progress.completed,
      },
    ];

    return baseStats.map((stat) => {
      const pending = Math.max(stat.total - stat.treated, 0);
      const percentage = stat.total === 0 ? 100 : Math.round((stat.treated / stat.total) * 100);
      return {
        ...stat,
        pending,
        percentage,
      };
    });
  }, [
    nonAttachedNotInRnicDecisions,
    nameMatchedPairs,
    addressDisplayEntries,
    immatMissingNotInRnicDecisions,
    otherVisibleNotInRnicDecisions,
    visibleRnicNotInFileDecisions,
    dbCoprosNotInFileDecisions,
    progress.completed,
    progress.total,
  ]);

  const syndicFeedbackClassification = useMemo(() => classifyManualTreatmentForPdf({
    matchedPairs,
    notInRnicDecisions,
    rnicNotInFileDecisions,
    fileAddressMap,
    rnicAddressMap,
    importSuccessCopros: successCopros,
  }), [matchedPairs, notInRnicDecisions, rnicNotInFileDecisions, fileAddressMap, rnicAddressMap, successCopros]);

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
        <span className="ml-3 text-gray-600">Analyse des anomalies...</span>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Warning - shown on smaller screens */}
      <div className="flex flex-col items-center justify-center px-4 py-16 lg:hidden">
        <ComputerDesktopIcon className="mb-6 h-20 w-20 text-rgpd-blue-light" />
        <h3 className="mb-3 text-center text-xl font-semibold text-gray-800">
          Écran trop petit
        </h3>
        <p className="mb-2 max-w-md text-center text-gray-600">
          Cette section de traitement manuel nécessite un affichage plus large pour une expérience optimale.
        </p>
        <p className="max-w-md text-center text-gray-600">
          Veuillez vous connecter depuis un <span className="font-semibold">ordinateur</span> ou une <span className="font-semibold">tablette en mode paysage</span>.
        </p>
      </div>

      {/* Main content - hidden on smaller screens */}
      <div ref={treatmentSectionRef} className="hidden space-y-6 lg:block">
        {/* Progress Header */}
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-800">Progression du traitement</h3>
              <a
                href="https://www.registre-coproprietes.gouv.fr/annuaire"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded border border-rgpd-blue-light bg-white px-3 py-1.5 text-sm font-medium text-rgpd-blue-dark transition-colors hover:bg-rgpd-blue-light hover:text-white"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Annuaire du Registre
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Badge
                mode={progress.isComplete ? 'success' : 'warning'}
                text={`${progress.completed}/${progress.total} traités (${progress.percentage}%)`}
              />
            </div>
          </div>
          <div className="h-2.5 w-full rounded-full bg-gray-200">
            <div
              className={`h-2.5 rounded-full transition-all ${
                progress.isComplete ? 'bg-green-500' : 'bg-rgpd-blue-dark'
              }`}
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>

        <StepNavigation
          steps={steps}
          activeStep={activeStep}
          onStepSelect={handleStepSelect}
          onPrev={handlePrevStep}
          onNext={handleNextStep}
          isSaving={saving || autoSavingStep || applying}
        />

        {activeStep === 'download' && (
          <div className="flex flex-col gap-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-blue-900">Télécharger les copros non rattachées à ce syndic</h3>
                <p className="text-sm text-blue-800">
                  Exporte la liste des copropriétés du RNIC non rattachées à ce syndic pour suivi.
                </p>
              </div>
              <span className="text-sm font-medium text-blue-900">
                {nonAttachedNotInRnicDecisions.length} entrée(s) non rattachée(s)
              </span>
            </div>
            {nonAttachedNotInRnicDecisions.length === 0 ? (
              <div className="rounded-lg border border-dashed border-blue-200 bg-white px-4 py-6 text-center text-sm text-blue-800">
                Aucune copropriété non rattachée à ce syndic. Vous pouvez passer à l'étape suivante.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDownloadNonAttached}
                    disabled={saving || applying}
                    className="flex items-center gap-2 rounded bg-rgpd-blue-dark px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rgpd-blue disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Télécharger
                  </button>
                  {downloadCompleted && (
                    <span className="flex items-center gap-1 text-sm text-green-700">
                      <CheckCircleIcon className="h-4 w-4" />
                      Téléchargement effectué et entrées forcées en copropriété
                    </span>
                  )}
                </div>
                <div className="text-xs text-blue-800">
                  Astuce : après téléchargement, toutes les entrées sont marquées « forcer copro ». Vous pouvez revenir en arrière si nécessaire.
                </div>
              </>
            )}
          </div>
        )}

        {activeStep === 'name' && (
          <NameMatchStep
            pairs={nameMatchedPairs}
            pendingCount={pendingNameMatches.length}
            expandedPairKey={expandedPairKey}
            onToggleExpand={setExpandedPairKey}
            onChangeStatus={handleMatchStatusChange}
            fileAddressMap={fileAddressMap}
            rnicAddressMap={rnicAddressMap}
          />
        )}

        {activeStep === 'address' && (
          <AddressMatchStep
            entries={addressDisplayEntries}
            fileAddressMap={fileAddressMap}
            rnicAddressMap={rnicAddressMap}
            onAccept={handleAcceptAddressSuggestion}
            onReject={handleRejectAddressSuggestion}
            onChangeStatus={handleMatchStatusChange}
          />
        )}

        {activeStep === 'typeCheckbox' && (
          <TypeCheckboxStep
            entries={immatMissingNotInRnicDecisions}
            fileAddressMap={fileAddressMap}
            onSelectType={handleImmatMissingTypeSelect}
            onIgnore={handleToggleImmatMissingIgnore}
          />
        )}

        {activeStep === 'nontrouve' && (
          <TypeSelectionStep
            entries={filteredNonTrvNotInRnicDecisions}
            totalVisible={totalNonTrvNotInRnicDecisions.length}
            search={notInRnicSearch}
            onSearchChange={setNotInRnicSearch}
            onClearSearch={() => setNotInRnicSearch('')}
            filteredCount={filteredNonTrvNotInRnicDecisions.length}
            onExport={handleExportNotInRnic}
            onDecision={handleNotInRnicDecision}
            onBulkDecision={handleBulkNotInRnicDecision}
            editingEntryKey={editingEntryKey}
            editingImmatValue={editingImmatValue}
            onStartEditImmat={handleStartEditImmat}
            onConfirmEdit={handleConfirmImmatCorrection}
            onCancelEdit={handleCancelEditImmat}
            setEditingImmatValue={setEditingImmatValue}
            fileAddressMap={fileAddressMap}
          />
        )}

        {activeStep === 'residual' && (
          <ResidualReviewStep
            notInRnicEntries={filteredNonTrvNotInRnicDecisions}
            rnicNotInFileEntries={filteredRnicNotInFileDecisions}
            notInRnicSearch={notInRnicSearch}
            onNotInRnicSearchChange={setNotInRnicSearch}
            onClearNotInRnicSearch={() => setNotInRnicSearch('')}
            filteredNotInRnicCount={filteredNonTrvNotInRnicDecisions.length}
            totalNotInRnic={totalNonTrvNotInRnicDecisions.length}
            rnicNotInFileSearch={rnicNotInFileSearch}
            onRnicNotInFileSearchChange={setRnicNotInFileSearch}
            onClearRnicNotInFileSearch={() => setRnicNotInFileSearch('')}
            filteredRnicNotInFileCount={filteredRnicNotInFileDecisions.length}
            totalRnicNotInFile={visibleRnicNotInFileDecisions.length}
            onExportNotInRnic={handleExportNotInRnic}
            onExportRnicNotInFile={handleExportRnicNotInFile}
            onNotInRnicDecision={handleNotInRnicDecision}
            onBulkNotInRnicDecision={handleBulkNotInRnicDecision}
            onRnicDecision={handleRnicNotInFileDecision}
            onBulkRnicDecision={handleBulkRnicNotInFileDecision}
            editingEntryKey={editingEntryKey}
            editingImmatValue={editingImmatValue}
            onStartEditImmat={handleStartEditImmat}
            onConfirmEdit={handleConfirmImmatCorrection}
            onCancelEdit={handleCancelEditImmat}
            setEditingImmatValue={setEditingImmatValue}
            typeDropdownEntryKey={typeDropdownEntryKey}
            onToggleTypeDropdown={setTypeDropdownEntryKey}
            typeDropdownRef={typeDropdownRef}
            fileAddressMap={fileAddressMap}
            rnicAddressMap={rnicAddressMap}
            bulkForceDropdownOpen={bulkForceDropdownOpen}
            setBulkForceDropdownOpen={setBulkForceDropdownOpen}
            bulkForceDropdownRef={bulkForceDropdownRef}
          />
        )}

        {activeStep === 'coprosPerdues' && dbCoprosNotInFileDecisions.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
            Aucune copropriété perdue à traiter. Vous pouvez passer à l'étape suivante.
          </div>
        )}

        {activeStep === 'coprosPerdues' && dbCoprosNotInFileDecisions.length > 0 && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-800">
                  Copros perdues ({dbCoprosNotInFileDecisions.filter((d) => d.decision !== 'pending').length}/{dbCoprosNotInFileDecisions.length} traitées)
                </h3>
                <p className="text-xs text-gray-500">
                  Copropriétés validées en base mais absentes du nouveau fichier. Choisissez de les désactiver ou de les conserver.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setDbCoprosNotInFileDecisions((prev) => prev.map((d) => ({ ...d, decision: 'deactivate' })))}
                  className="rounded border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
                >
                  Tout désactiver
                </button>
                <button
                  onClick={() => setDbCoprosNotInFileDecisions((prev) => prev.map((d) => ({ ...d, decision: 'keep' })))}
                  className="rounded border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100"
                >
                  Tout conserver
                </button>
              </div>
            </div>

            <div className="relative">
              <input
                type="text"
                value={coprosPerduesSearch}
                onChange={(e) => setCoprosPerduesSearch(e.target.value)}
                placeholder="Rechercher par nom ou immatriculation..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-rgpd-blue-dark focus:ring-1 focus:ring-rgpd-blue-dark"
              />
              {coprosPerduesSearch && (
                <button onClick={() => setCoprosPerduesSearch('')} className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600">&times;</button>
              )}
            </div>

            <div className="max-h-[60vh] space-y-2 overflow-y-auto">
              {dbCoprosNotInFileDecisions
                .filter((d) => {
                  if (!coprosPerduesSearch) { return true; }
                  const q = coprosPerduesSearch.toLowerCase();
                  return d.name.toLowerCase().includes(q) || d.immatriculation.toLowerCase().includes(q);
                })
                .map((entry) => (
                  <div
                    key={entry.coproId}
                    className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${
                      entry.decision === 'deactivate' ? 'border-red-200 bg-red-50' :
                      entry.decision === 'keep' ? 'border-green-200 bg-green-50' :
                      'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-800">{entry.name}</p>
                      <div className="flex gap-3 text-xs text-gray-500">
                        {entry.immatriculation && <span>Immat: {entry.immatriculation}</span>}
                        {entry.type != null && <span>Type: {coproTypes[entry.type as keyof typeof coproTypes] || 'Inconnu'}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        onClick={() => setDbCoprosNotInFileDecisions((prev) =>
                          prev.map((d) => d.coproId === entry.coproId ? { ...d, decision: 'deactivate' } : d),
                        )}
                        className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                          entry.decision === 'deactivate'
                            ? 'bg-red-600 text-white'
                            : 'border border-red-300 text-red-700 hover:bg-red-50'
                        }`}
                      >
                        Désactiver
                      </button>
                      <button
                        onClick={() => setDbCoprosNotInFileDecisions((prev) =>
                          prev.map((d) => d.coproId === entry.coproId ? { ...d, decision: 'keep' } : d),
                        )}
                        className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                          entry.decision === 'keep'
                            ? 'bg-green-600 text-white'
                            : 'border border-green-300 text-green-700 hover:bg-green-50'
                        }`}
                      >
                        Conserver
                      </button>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {activeStep === 'summary' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {summaryStepStats.map((stat) => (
                <div
                  key={stat.id}
                  className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Étape</p>
                      <p className="text-sm font-semibold text-gray-800">{stat.label}</p>
                      {stat.description && (
                        <p className="mt-1 text-xs text-gray-500">{stat.description}</p>
                      )}
                    </div>
                    {stat.id !== 'summary' && (
                      <button
                        onClick={() => handleStepSelect(stat.id)}
                        className="text-xs font-semibold text-rgpd-blue-dark hover:underline"
                      >
                        Ouvrir
                      </button>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-gray-800">
                        {stat.treated}/{stat.total || 0} traité(s)
                      </span>
                      <span className={stat.pending > 0 ? 'text-xs text-amber-600' : 'text-xs text-green-600'}>
                        {stat.pending > 0 ? `${stat.pending} en attente` : 'Terminé'}
                      </span>
                    </div>
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className={`${stat.pending > 0 ? 'bg-amber-500' : 'bg-green-500'} h-2 rounded-full transition-all`}
                        style={{ width: `${stat.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Synthese import diff panel -- only shown when diff data exists */}
            {(dbCoprosNotInFileDecisions.length > 0 || effectiveFileCoprosNotInDb.length > 0) && (
              <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/30 p-5">
                <h3 className="mb-4 text-base font-bold text-indigo-900">Synthèse import diff</h3>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Copros perdues */}
                  {dbCoprosNotInFileDecisions.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-white p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="text-sm font-bold text-red-700">Copros perdues</h4>
                        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-700">
                          -{dbCoprosNotInFileDecisions.length}
                        </span>
                      </div>
                      <div className="mb-2 flex gap-3 text-xs text-gray-600">
                        <span>{dbCoprosNotInFileDecisions.filter((d) => d.decision === 'deactivate').length} désactivée(s)</span>
                        <span>{dbCoprosNotInFileDecisions.filter((d) => d.decision === 'keep').length} conservée(s)</span>
                        {dbCoprosNotInFileDecisions.some((d) => d.decision === 'pending') && (
                          <span className="text-amber-600">{dbCoprosNotInFileDecisions.filter((d) => d.decision === 'pending').length} en attente</span>
                        )}
                      </div>
                      <details className="group">
                        <summary className="cursor-pointer text-xs font-medium text-red-600 hover:text-red-800">
                          Voir la liste
                        </summary>
                        <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                          {dbCoprosNotInFileDecisions.map((d) => (
                            <div key={d.coproId} className={`flex items-center justify-between rounded px-2 py-1 text-xs ${
                              d.decision === 'deactivate' ? 'bg-red-50 text-red-800' :
                              d.decision === 'keep' ? 'bg-green-50 text-green-800' :
                              'bg-gray-50 text-gray-700'
                            }`}>
                              <span className="truncate">{d.name}</span>
                              <span className="ml-2 shrink-0 font-medium">
                                {d.decision === 'deactivate' ? 'Désactivée' : d.decision === 'keep' ? 'Conservée' : 'En attente'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}

                  {/* Copros gagnées */}
                  {effectiveFileCoprosNotInDb.length > 0 && (
                    <div className="rounded-lg border border-green-200 bg-white p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="text-sm font-bold text-green-700">Copros gagnées</h4>
                        <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-bold text-green-700">
                          +{effectiveFileCoprosNotInDb.length}
                        </span>
                      </div>
                      <p className="mb-2 text-xs text-gray-600">
                        Nouvelles copros du fichier absentes de la base
                      </p>
                      <details className="group">
                        <summary className="cursor-pointer text-xs font-medium text-green-600 hover:text-green-800">
                          Voir la liste
                        </summary>
                        <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                          {effectiveFileCoprosNotInDb.map((c, idx) => (
                            <div key={`${c.immatriculation}-${idx}`} className="flex items-center justify-between rounded bg-green-50 px-2 py-1 text-xs text-green-800">
                              <span className="truncate">{c.name}</span>
                              {c.immatriculation && <span className="ml-2 shrink-0 font-mono text-[10px]">{c.immatriculation}</span>}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                </div>

                {/* Net change */}
                <div className="mt-4 flex items-center justify-center gap-4 rounded-lg border border-indigo-200 bg-white px-4 py-3">
                  <span className="text-sm font-medium text-gray-600">Variation nette du portefeuille :</span>
                  <span className="text-lg font-bold text-green-600">+{effectiveFileCoprosNotInDb.length}</span>
                  <span className="text-lg text-gray-400">/</span>
                  <span className="text-lg font-bold text-red-600">-{dbCoprosNotInFileDecisions.filter((d) => d.decision === 'deactivate').length}</span>
                  <span className="text-lg text-gray-400">=</span>
                  {(() => {
                    const net = effectiveFileCoprosNotInDb.length - dbCoprosNotInFileDecisions.filter((d) => d.decision === 'deactivate').length;
                    return <span className={`text-lg font-bold ${net >= 0 ? 'text-green-700' : 'text-red-700'}`}>{net >= 0 ? '+' : ''}{net}</span>;
                  })()}
                </div>

                <div className="mt-3 flex justify-center">
                  <button
                    onClick={() => {
                      const rows: Record<string, string>[] = [];
                      dbCoprosNotInFileDecisions.forEach((d) => {
                        rows.push({
                          'Type': 'Perdue',
                          'Nom': d.name,
                          'Immatriculation': d.immatriculation || '',
                          'Type copro': d.type != null ? (coproTypes[d.type as keyof typeof coproTypes] || '') : '',
                          'Décision': d.decision === 'deactivate' ? 'Désactivée' : d.decision === 'keep' ? 'Conservée' : 'En attente',
                        });
                      });
                      effectiveFileCoprosNotInDb.forEach((c) => {
                        rows.push({
                          'Type': 'Gagnée',
                          'Nom': c.name,
                          'Immatriculation': c.immatriculation || '',
                          'Type copro': '',
                          'Décision': 'Nouvelle',
                        });
                      });
                      const ws = XLSX.utils.json_to_sheet(rows);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Diff Import');
                      XLSX.writeFile(wb, `synthese-import-diff-${new Date().toISOString().split('T')[0]}.xlsx`);
                    }}
                    className="flex items-center gap-1 rounded border border-indigo-400 bg-indigo-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-indigo-700"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Synthèse import diff (Excel)
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Exports des traitements</p>
                  <p className="text-xs text-gray-500">
                    Téléchargez un rapport pouvant être envoyé au syndic pour qu'il puisse ajuster les écarts dans son logiciel métier et valider l'import.
                  </p>
                </div>
                <div className="my-6 flex w-full items-center justify-center gap-3">
                  <button
                    onClick={handleDownloadPdfSummary}
                    className="flex items-center gap-1 rounded border border-rgpd-blue-dark bg-rgpd-blue-dark px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-rgpd-blue disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={generatingPdf}
                    title="Générer le PDF récapitulatif du traitement manuel"
                  >
                    {generatingPdf ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
                    Synthèse PDF
                  </button>
                  <button
                    onClick={handleCopyMailText}
                    className="flex items-center gap-1 rounded border border-rgpd-blue-dark bg-white px-3 py-2 text-xs font-medium text-rgpd-blue-dark transition-colors hover:bg-rgpd-blue-dark hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    title="Copier le texte du mail d'accompagnement dans le presse-papiers"
                  >
                    {mailCopied ? <CheckCircleIcon className="h-4 w-4 text-green-500" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
                    {mailCopied ? 'Copié !' : 'Copier le texte du mail'}
                  </button>
                </div>
              </div>
              {pdfErrors.length > 0 && (
                <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                  <p className="text-sm font-semibold">PDF non généré : corrigez ces incohérences</p>
                  <ul className="list-inside list-disc space-y-1">
                    {pdfErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <ResidualReviewStep
              notInRnicEntries={filteredNonTrvNotInRnicDecisions}
              rnicNotInFileEntries={filteredRnicNotInFileDecisions}
              notInRnicSearch={notInRnicSearch}
              onNotInRnicSearchChange={setNotInRnicSearch}
              onClearNotInRnicSearch={() => setNotInRnicSearch('')}
              filteredNotInRnicCount={filteredNonTrvNotInRnicDecisions.length}
              totalNotInRnic={totalNonTrvNotInRnicDecisions.length}
              rnicNotInFileSearch={rnicNotInFileSearch}
              onRnicNotInFileSearchChange={setRnicNotInFileSearch}
              onClearRnicNotInFileSearch={() => setRnicNotInFileSearch('')}
              filteredRnicNotInFileCount={filteredRnicNotInFileDecisions.length}
              totalRnicNotInFile={visibleRnicNotInFileDecisions.length}
              onExportNotInRnic={handleExportNotInRnic}
              onExportRnicNotInFile={handleExportRnicNotInFile}
              onNotInRnicDecision={handleNotInRnicDecision}
              onBulkNotInRnicDecision={handleBulkNotInRnicDecision}
              onRnicDecision={handleRnicNotInFileDecision}
              onBulkRnicDecision={handleBulkRnicNotInFileDecision}
              editingEntryKey={editingEntryKey}
              editingImmatValue={editingImmatValue}
              onStartEditImmat={handleStartEditImmat}
              onConfirmEdit={handleConfirmImmatCorrection}
              onCancelEdit={handleCancelEditImmat}
              setEditingImmatValue={setEditingImmatValue}
              typeDropdownEntryKey={typeDropdownEntryKey}
              onToggleTypeDropdown={setTypeDropdownEntryKey}
              typeDropdownRef={typeDropdownRef}
              fileAddressMap={fileAddressMap}
              rnicAddressMap={rnicAddressMap}
              bulkForceDropdownOpen={bulkForceDropdownOpen}
              setBulkForceDropdownOpen={setBulkForceDropdownOpen}
              bulkForceDropdownRef={bulkForceDropdownRef}
            />
          </div>
        )}

        {activeStep === 'syndicFeedback' && (
          <SyndicFeedbackStep
            classification={syndicFeedbackClassification}
            overrides={syndicFeedbackOverrides}
            onOverridesChange={setSyndicFeedbackOverrides}
            hasBeenApplied={hasBeenApplied}
            onApplyFeedbackClick={handleApplyFeedbackClick}
            applyingFeedback={applyingFeedback}
            saving={saving}
          />
        )}

        {/* Empty State */}
        {visibleMatchedPairs.length === 0 && visibleNotInRnicDecisions.length === 0 && visibleRnicNotInFileDecisions.length === 0 && (
          <div className="py-12 text-center">
            <CheckCircleIcon className="mx-auto mb-4 h-16 w-16 text-green-500" />
            <h3 className="mb-2 text-lg font-semibold text-gray-800">Aucune anomalie à traiter</h3>
            <p className="text-gray-600">
              Toutes les entrées de cet import correspondent parfaitement au RNIC.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
          <div className="text-sm text-gray-500">
            {(activeStep === 'summary' || activeStep === 'syndicFeedback') && !progress.isComplete && !hasBeenApplied && (
              <span className="flex items-center gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                Traitez toutes les entrées pour pouvoir importer
              </span>
            )}
            {(activeStep === 'summary' || activeStep === 'syndicFeedback') && hasBeenApplied && (
              <span className="flex items-center gap-2">
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
                <span className="text-green-700">
                  Import déjà appliqué
                  {appliedImportId && <> (import #{appliedImportId})</>}
                </span>
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleNextStep}
              disabled={saving || applying || applyingFeedback || autoSavingStep || stepOrder.indexOf(activeStep) === stepOrder.length - 1}
              className="flex items-center gap-2 rounded bg-rgpd-blue-dark px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-rgpd-blue disabled:cursor-not-allowed disabled:opacity-50"
            >
              Étape suivante
            </button>
            
            {onApplyImport && !hasBeenApplied && (activeStep === 'summary' || activeStep === 'syndicFeedback') && (
              <button
                onClick={handleApplyImportClick}
                disabled={saving || applying || autoSavingStep || !progress.isComplete || !hasOriginalFile}
                className="flex items-center gap-2 rounded bg-rgpd-green px-6 py-3 text-sm font-medium text-white transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                title={!progress.isComplete ? 'Toutes les entrées doivent être traitées' : !hasOriginalFile ? 'Fichier original non disponible' : 'Appliquer l\'import avec ce traitement'}
              >
                {applying ? (
                  <>
                    <Spinner size="sm" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    Importer avec ce traitement
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Apply Import Confirmation Dialog */}
      <ConfirmationDialog
        open={showApplyConfirmation}
        title={<><span role="img" aria-label="warning">⚠️</span> Attention : Base réelle</>}
        confirmLabel="Oui, importer maintenant"
        cancelLabel="Annuler"
        onConfirm={handleApplyImportConfirm}
        onClose={() => setShowApplyConfirmation(false)}
        loading={applying}
        description={(
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 shrink-0 text-amber-500" />
              <div className="space-y-2">
                <p className="font-medium text-amber-800">
                  Vous êtes sur le point d'importer en base réelle avec le traitement manuel.
                </p>
                <p className="text-sm text-amber-700">
                  Cette action va importer et valider les éléments du fichier selon vos décisions,
                  les rendant immédiatement accessibles à l'utilisateur final.
                </p>
              </div>
            </div>
            
            <div className="space-y-2 text-gray-700">
              <p className="font-medium">Résumé du traitement :</p>
              <ul className="ml-2 list-inside list-disc space-y-1 text-sm text-gray-600">
                <li>{visibleMatchedPairs.filter((p) => p.status === 'validated').length} correspondance(s) validée(s) entre le fichier d'import et le RNIC</li>
                <li>{visibleNotInRnicDecisions.filter((d) => d.decision === 'force').length} entrée(s) du fichier d'import forcée(s)</li>
                <li>{visibleNotInRnicDecisions.filter((d) => d.decision === 'skip').length} entrée(s) du fichier d'import ignorée(s)</li>
                <li>{visibleRnicNotInFileDecisions.filter((d) => d.decision === 'integrate').length} entrée(s) RNIC intégrée(s)</li>
              </ul>
            </div>

            <p className="pt-2 text-center font-semibold text-gray-800">
              Êtes-vous sûr de vouloir continuer ?
            </p>
          </div>
        )}
      />

      {/* Apply Feedback Confirmation Dialog */}
      <ConfirmationDialog
        open={showFeedbackConfirmation}
        title={<><span role="img" aria-label="tool">🔧</span> Appliquer les retours syndic</>}
        confirmLabel="Oui, appliquer les modifications"
        cancelLabel="Annuler"
        onConfirm={handleApplyFeedbackConfirm}
        onClose={() => setShowFeedbackConfirmation(false)}
        loading={applyingFeedback}
        description={(
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 shrink-0 text-blue-500" />
              <div className="space-y-2">
                <p className="font-medium text-blue-800">
                  Vous êtes sur le point de modifier la base réelle en fonction des retours du syndic.
                </p>
                <p className="text-sm text-blue-700">
                  Seules les différences seront appliquées : nouvelles copropriétés à importer,
                  changements d'immatriculation ou de type.
                </p>
              </div>
            </div>

            <div className="space-y-2 text-gray-700">
              <p className="font-medium">{syndicFeedbackOverrides.length} modification(s) à appliquer :</p>
              <ul className="ml-2 list-inside list-disc space-y-1 text-sm text-gray-600">
                {syndicFeedbackOverrides.some((o) => o.newType !== undefined) && (
                  <li>{syndicFeedbackOverrides.filter((o) => o.newType !== undefined).length} changement(s) de type</li>
                )}
                {syndicFeedbackOverrides.some((o) => o.newImmat !== undefined) && (
                  <li>{syndicFeedbackOverrides.filter((o) => o.newImmat !== undefined).length} changement(s) d'immatriculation</li>
                )}
              </ul>
            </div>

            <p className="pt-2 text-center font-semibold text-gray-800">
              Êtes-vous sûr de vouloir continuer ?
            </p>
          </div>
        )}
      />
    </>
  );
};

export default ManualTreatmentTab;
